import { Box, Divider, Grid2, Paper, styled, Typography } from "@mui/material";
import React, { useEffect, useCallback, useState } from "react";
import DeviceHealthRow from "./DeviceHealthRow";
import DeviceHealthActions from "./DeviceHealthActions";
import DeviceHealthDownloadDataDialog from "./DeviceHealthDownloadDataDialog";
import { getIoTAttributeMeta, getIoTStateData } from "../../../../Api/IoTApi";
import { useBatteryInfo } from "../../../../Pages/DigitalTwinV3/DigitalTwinContext";
import { isSignalQualityAttribute } from "../../components/Header/headerConfig";

const PageContainer = styled(Box)(({ theme }) => ({
    marginTop: '20px',
    width: '100%',
    minHeight: '200px',
}))

export default function DeviceHealth({ downloadMaxHours }) {

    const { batteryID } = useBatteryInfo()

    const [showDownloadDataPopup, setShowDownloadDataPopup] = useState(false);
    const [iotAttributeMeta, setIotAttributeMeta] = useState([]);
    const [attributesData, setAttributesData] = useState([]);

    const closeDownloadDataPopup = () => {
        setShowDownloadDataPopup(false);
    }

    const fetchData = async () => {
        const [attributeMetaResponse, attributeDataResponse] = await Promise.all([
            getIoTAttributeMeta(),
            getIoTStateData(batteryID),
        ]);
        const attributesMeta = attributeMetaResponse?.response?.data?.attributesMeta || [];
        const attributesResponseData = attributeDataResponse?.response?.data || {};
        if (attributesMeta && attributesMeta.length > 0) {
            const grouped = attributesMeta.reduce((acc, attr) => {
              const key = attr.signalType || "unknown";
              if (!acc[key]) {
                acc[key] = [];
              }
              acc[key].push(attr);
              return acc;
            }, {});

            setIotAttributeMeta(grouped);
        }
        setAttributesData(attributesResponseData);
    }

    useEffect(() => {
        fetchData();
    }, [])

    return (
        <>
            <Grid2 sx={{ mb: 10 }}>
                <PageContainer>
                    <Paper elevation={0}
                        sx={{
                            px: 5,
                            py: 4,
                            height: "100%",
                            borderRadius: "8px",
                            border: "1px solid #E6E6E6" ,
                        }}
                    >
                        <Grid2 container justifyContent={'space-between'} alignItems={'center'} mb={2}>
                            <Typography variant="fw500s20c000000" sx={{lineHeight: '24px'}}>Device Health Data</Typography>
                            <DeviceHealthActions actions={[
                                { type: 'nothing', label: 'Download Data', icon: 'download-file-icon', onClick: () => setShowDownloadDataPopup(true) },
                            ]} />
                            <DeviceHealthDownloadDataDialog open={showDownloadDataPopup} handleClose={closeDownloadDataPopup} maxHours={downloadMaxHours} />
                        </Grid2>
                        {Object.entries(iotAttributeMeta)
                            .sort(([keyA], [keyB]) => {
                                if (keyA === "other") return 1;   // push "other" down
                                if (keyB === "other") return -1;  // keep other keys before "other"
                                return 0;                         // keep original order for others
                            })
                            .map(([name, attributes]) => (
                            <Grid2 key={`attributeBlock-${name}`} container flexDirection={'column'} gap={2} mb={4}>
                                <Box>
                                    <Typography variant="fw500s16c000000" sx={{lineHeight: '24px'}}>Packet:&nbsp;</Typography>
                                    <Typography variant="fw500s16c000000" sx={{lineHeight: '24px', textTransform: name.toLowerCase() === 'gps' ? 'uppercase' : 'capitalize'}}>{name}</Typography>
                                </Box>
                                <Grid2 container rowGap={1} columnGap={4.5}>
                                    {attributes.map((attribute) => {
                                        let attributeType = attribute.attributeType?.toLowerCase();
                                        let label = attribute.attributeDisplayName;
                                        const value = attributesData?.[attribute.attributeName];
                                        const unit = (attributeType === 'decimal' || attributeType === 'int') ? attribute.unitOfMeasurement : null;
                                        // The IoT API names it ext_<signalType>_SIG_QUAL (ext_other_SIG_QUAL).
                                        if (isSignalQualityAttribute(attribute.attributeName)) {
                                            attributeType = 'signal_strength';
                                        }
                                        return <DeviceHealthRow
                                            key={`deviceHealthRow-${name}-${attribute.attributeName}`}
                                            label={label}
                                            value={value}
                                            type={attributeType}
                                            unit={unit}
                                        />
                                    })}
                                </Grid2>
                            </Grid2>
                        ))}
                    </Paper>
                </PageContainer>
            </Grid2>
        </>
    )
}
